// Seven-screen roller builder. Display-only estimates never become order dimensions.
(function(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.NoDrillBuilderSteps = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
  'use strict';
  const stages = Object.freeze(['installation_mount', 'hardware_finish', 'bottom_rail', 'fabric_color', 'control_method', 'measurements', 'review']);
  // Albert (Oct 17): room is required from this list; the window name is optional.
  const rooms = Object.freeze(['Living Room', 'Kitchen', 'Primary Bedroom', 'Bedroom 2', 'Bedroom 3', 'Bedroom 4', 'Bedroom 5',
    'Office', 'Dining Room', 'Bathroom', 'Other']);
  const roomReady = c => Boolean(String(c?.room || '').trim());
  // A blank name becomes "Room, Window N", numbered within that room ("Bedroom 2, Window 1").
  const autoLabel = (room, n) => room ? `${room}, Window ${n}` : `Window ${n}`;
  const autoPattern = room => new RegExp(`^${autoLabel(room, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`, 'i');
  const lineLabel = p => String(p['_Room / Window Label'] || p['Room / Window Label'] || '').trim();
  // Since Oct 13 the raw room is the hidden "_Room"; the visible "Room" line joins room and name.
  const lineRoom = p => String(('_Room / Window Label' in p ? p._Room : p.Room) || '').trim();
  // Highest window number already used in each room, counting shade lines only.
  function roomWindowCounts(cart, excludeId) {
    const seen = {};
    for (const item of cart?.items || []) {
      const p = item?.properties || {};
      if (p._ndr_line_role !== 'shade' || (excludeId && p._configuration_id === excludeId)) continue;
      const room = lineRoom(p);
      const entry = seen[room] || (seen[room] = {count:0, highest:0});
      entry.count += Math.max(1, Number(item.quantity) || 1);
      const match = autoPattern(room).exec(lineLabel(p));
      if (match) entry.highest = Math.max(entry.highest, Number(match[1]));
    }
    return Object.fromEntries(Object.entries(seen).map(([room, e]) => [room, Math.max(e.count, e.highest)]));
  }
  // A typed name always wins. An edited shade keeps its own automatic name while its room stays the same.
  function autoWindowName(c, counts = {}, own = null) {
    const typed = String(c?.roomLabel || '').trim();
    if (typed) return typed;
    const room = String(c?.room || '').trim();
    if (own && own.room === room && autoPattern(room).test(own.label)) return own.label;
    return autoLabel(room, (counts[room] || 0) + 1);
  }
  function nextWindowName(cart, excludeId, room = '') { return autoWindowName({room}, roomWindowCounts(cart, excludeId)); }
  function windowName(explicit, cart, excludeId, room = '') { return String(explicit || '').trim() || nextWindowName(cart, excludeId, room); }
  // Oct 23 step row (Albert, topic 39717: "a progress bar ... so you can navigate the steps a bit faster").
  // One short name per step, in the same order as stages.
  const stepLabels = Object.freeze(['Mount', 'Hardware', 'Rail', 'Color', 'Control', 'Size', 'Review']);
  // A step counts as finished when its answer is valid and the shopper has been on it. After the mount the builder
  // fills in hardware, rail, control and color by itself, so a valid default alone does not count. The size check
  // box is never ticked for the shopper, so a ticked one means they went through every step (here, in a cart edit,
  // or on the page they switched fabric from). Tapping opens finished steps and the first unfinished one, never a
  // step past it, so a change that breaks a later answer sends the shopper back through it.
  function stepStates(c, rules, core, index, visited, editing) {
    const last = stages.length - 1;
    const seen = step => Boolean(editing) || Boolean(c?.measurementConfirmed) || Boolean(visited?.has?.(step));
    const valid = step => {
      try { return Boolean(canAdvance(stages[step], c || {}, rules || {}, core)); } catch (error) { return false; }
    };
    let first = last;
    for (let step = 0; step < last; step += 1) if (!(valid(step) && seen(step))) { first = step; break; }
    return stages.map((stage, step) => ({stage, label:stepLabels[step], reachable:step <= first,
      state:step === index ? 'current' : step < first ? 'done' : step === first ? 'open' : 'locked'}));
  }
  function historyState(state, id, step, previous = null) { return {...(state || {}), ndrBuilder: {id, step, previous}}; }
  function historyStep(state, id) {
    const value = state?.ndrBuilder;
    return value?.id === id && Number.isInteger(value.step) && value.step >= 0 && value.step < stages.length ? value.step : null;
  }
  function canAdvance(stage, c, rules, core) {
    const o = c.options || {};
    if (stage === 'installation_mount') return o.installation_method === 'standard-bracket' && o.mount_location === 'outside'
      || o.installation_method === 'no-drill' && o.mount_location === 'inside' && Boolean(c.noDrillDepthConfirmed)
        && ['flat', 'textured'].includes(c.mountingSurface) && core.validateOpeningMaterial(c.openingMaterial, c.openingMaterialCautionAcknowledged).valid;
    if (stage === 'hardware_finish') return Boolean(o.hardware_finish);
    if (stage === 'bottom_rail') return Boolean(o.bottom_rail && (o.bottom_rail !== 'regular' || o.bottom_rail_finish));
    if (stage === 'fabric_color') return Boolean(o.fabric_color);
    if (stage === 'control_method') return Boolean(c.control && core.validateRollerAddons(c, rules).valid);
    if (stage === 'measurements') {
      const bounds = core.resolveMeasurementRules(c, rules);
      return Boolean(c.measurementConfirmed && rules.measurement?.status !== 'unresolved'
        && Number.isFinite(c.width) && Number.isFinite(c.height)
        && core.validateMeasurement(c.width, 'width', bounds).valid && core.validateMeasurement(c.height, 'height', bounds).valid);
    }
    return roomReady(c) && core.validateConfiguration(c, rules).valid;
  }
  // Black Friday sale (NDR-D-145, Albert, Nov 2 2026): 51% off every shade until Mon Nov 30 11:59:59 PM ET. Side
  // channels and remotes stay at the regular price. Code BF51 (added by the cart page) takes 51% off each shade line
  // at checkout; shade prices are whole dollars, so the sale price is exact to the cent. Display only: the cart request
  // keeps the regular price, which the cart client checks against the cart map.
  const SALE = Object.freeze({percent:51, startsAt:Date.parse('2026-11-02T05:00:00Z'), endsAt:Date.parse('2026-12-01T05:00:00Z')});
  const saleActive = (now = Date.now()) => now >= SALE.startsAt && now < SALE.endsAt;
  function salePrice(quote, now = Date.now()) {
    if (!saleActive(now) || !quote?.valid || !Number.isSafeInteger(quote.shadeCents) || !Number.isSafeInteger(quote.totalCents)) return null;
    const offCents = Math.round(quote.shadeCents * SALE.percent / 100);
    return offCents > 0 ? {totalCents:quote.totalCents - offCents, offCents} : null;
  }
  function displayPrice(c, pricing, rules, core) {
    if (!pricing || !rules || !core || rules.measurement?.status === 'unresolved') return {valid:false};
    const sized = Number.isFinite(c.width) && Number.isFinite(c.height);
    const mounts = c.options?.installation_method ? [c.options.installation_method] : ['no-drill', 'standard-bracket'];
    const controls = c.control ? [c.control] : ['cordless', 'motorized'];
    const quotes = [];
    for (const mount of mounts) for (const control of controls) {
      const options = {...c.options, installation_method:mount,
        mount_location:mount === 'no-drill' ? 'inside' : 'outside',
        headrail_style:mount === 'no-drill' ? 'no-drill-square-cassette' : 'standard-bracket-cassette',
        hardware_finish:c.options?.hardware_finish || 'white', bottom_rail:c.options?.bottom_rail || 'regular',
        bottom_rail_finish:c.options?.bottom_rail_finish || c.options?.hardware_finish || 'white'};
      const bounds = core.resolveMeasurementRules({...c, options, control}, rules);
      const probe = {...c, options, control, accessories:c.accessories || [],
        width:Number.isFinite(c.width) ? c.width : bounds.minWidth,
        height:Number.isFinite(c.height) ? c.height : bounds.minHeight};
      const shade = pricing.priceConfiguration(probe);
      if (!shade.valid) continue;
      const channel = pricing.priceSideChannel({height:probe.height, sideChannel:c.sideChannel || 'none', fabricFamily:options.fabric_family});
      const remote = control === 'motorized' && c.remoteType && c.remoteType !== 'none'
        ? pricing.priceRemote({type:c.remoteType, quantity:c.remoteQuantity}) : {valid:true,totalCents:0};
      if (!channel.valid || !remote.valid) continue;
      quotes.push({...shade,shadeCents:shade.totalCents,totalCents:shade.totalCents + channel.totalCents + remote.totalCents});
    }
    if (!quotes.length) return {valid:false};
    const quote = quotes.reduce((a,b) => a.totalCents <= b.totalCents ? a : b);
    return {...quote, starting:!sized || !c.control || !c.options?.installation_method
      || (c.control === 'motorized' && !c.remoteType)};
  }
  // Short review rows from the approved mockup. Each row returns to the step that sets it.
  function reviewRows(c, rules, core) {
    const o = c.options || {};
    const none = 'Not chosen';
    const label = (group, id) => rules.optionGroups?.find(g => g.id === group)?.options.find(x => x.id === id)?.label;
    const mountSystem = c.mountSystem || (o.installation_method === 'no-drill' ? 'no-drill-inside'
      : o.installation_method === 'standard-bracket' ? 'standard-outside' : '');
    const rail = label('bottom_rail', o.bottom_rail)?.replace(/\s*Bottom Rail$/i, '');
    const railFinish = o.bottom_rail === 'regular' ? label('bottom_rail_finish', o.bottom_rail_finish) : '';
    const size = Number.isFinite(c.width) && Number.isFinite(c.height)
      ? `${core.formatInches(c.width).replace(/ in$/, '')} x ${core.formatInches(c.height)}` : none;
    const rows = [
      {label:'Mount', stage:'installation_mount', value:rules.mounts?.[mountSystem]?.label || label('installation_method', o.installation_method) || none},
      {label:'Hardware', stage:'hardware_finish', value:label('hardware_finish', o.hardware_finish) || none},
      {label:'Bottom rail', stage:'bottom_rail', value:rail ? (railFinish ? `${rail} · ${railFinish}` : rail) : none},
      {label:'Color', stage:'fabric_color', value:label('fabric_color', o.fabric_color) || none},
      {label:'Control', stage:'control_method', value:rules.controls?.[c.control]?.label || none},
    ];
    if (c.control === 'motorized') {
      const remote = {one_channel:'1-channel remote', six_channel:'6-channel remote', sixteen_channel:'16-channel remote', none:'No remote with this shade'}[c.remoteType];
      rows.push({label:'Remote', stage:'control_method', value:remote
        ? (c.remoteType === 'none' ? remote : `${remote} × ${Math.max(1, Number(c.remoteQuantity) || 1)}`) : none});
    }
    const channel = {l_channel:'L channel', u_channel:'U channel'}[c.sideChannel];
    if (channel) rows.push({label:'Side channels', stage:'control_method', value:channel});
    const extras = (c.accessories || []).map(id => rules.accessories?.[id]?.label).filter(Boolean);
    if (extras.length) rows.push({label:'Add-ons', stage:'control_method', value:extras.join(', ')});
    rows.push({label:'Size', stage:'measurements', value:size});
    return rows;
  }
  // Steps before the size have no exact price yet. The bottom bar already shows the From price,
  // so a choice card must not say "Price pending". Real "+$" adders stay.
  function hidePendingBadges(section, c) {
    if (Number.isFinite(c.width) && Number.isFinite(c.height)) return;
    section.querySelectorAll('.ndr-choice__price').forEach(node => {
      if (/^\s*Price pending\s*$/i.test(node.textContent || '')) node.hidden = true;
    });
  }
  // "Add another window" keeps the mount, fabric and every choice. It clears only this window's
  // size (with its measuring check), room, name and fit check, the way a fresh builder starts.
  function resetForAnotherWindow(form) {
    const field = name => form.elements[name];
    ['widthWhole', 'heightWhole', 'room', 'roomLabel'].forEach(name => { if (field(name)) field(name).value = ''; });
    ['widthFraction', 'heightFraction'].forEach(name => { if (field(name)) field(name).value = '0'; });
    ['measurementConfirmed', 'compatibilityConfirmed'].forEach(name => { if (field(name)) field(name).checked = false; });
    delete form.dataset.heightEntered;
  }
  function setup(section, rules, core) {
    if (section.dataset.builderSteps !== 'true' || section.builderSteps) return null;
    const form = section.querySelector('[data-configurator-form]');
    const next = section.querySelector('[data-builder-next]');
    const back = section.querySelector('[data-builder-back]');
    const add = section.querySelector('[data-add-another-window]');
    const progress = section.querySelector('[data-builder-progress]');
    const bar = section.querySelector('[data-builder-progress-bar]');
    const price = section.querySelector('[data-builder-price]');
    const regular = section.querySelector('[data-builder-regular]');
    const history = globalThis.history;
    const id = globalThis.location.pathname;
    let index = historyStep(history.state, id) ?? 0;
    const visited = new Set();
    let cartCounts = {};
    let ownLine = null;
    const configuration = () => JSON.parse(section.dataset.configuration || '{}');
    // Oct 23 step row: the thin bar becomes seven steps you can tap. The old bar stays in the page (hidden) for
    // anything that still reads it. Phones show the current step's name next to "Step N of 7".
    const progressBox = progress?.closest('.ndr-builder-progress');
    let nav = null;
    let stepName = null;
    if (progressBox) {
      const doc = section.ownerDocument;
      stepName = doc.createElement('span');
      stepName.className = 'ndr-builder-progress__name';
      stepName.dataset.builderStepName = '';
      stepName.setAttribute('aria-hidden', 'true');
      progress.after(stepName);
      nav = doc.createElement('ol');
      nav.className = 'ndr-builder-stepnav';
      nav.dataset.builderStepNav = '';
      nav.setAttribute('aria-label', 'Steps');
      stepLabels.forEach((label, step) => {
        const item = doc.createElement('li');
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'ndr-builder-stepnav__step';
        button.dataset.stepIndex = String(step);
        const text = doc.createElement('span');
        text.textContent = label;
        button.append(text);
        item.append(button);
        nav.append(item);
      });
      progressBox.append(nav);
      progressBox.dataset.stepNav = 'true';
    }
    function paintSteps(c) {
      if (!nav) return;
      const states = stepStates(c, rules, core, index, visited, Boolean(section.dataset.editLine));
      stepName.textContent = stepLabels[index] || '';
      nav.querySelectorAll('[data-step-index]').forEach(button => {
        const s = states[Number(button.dataset.stepIndex)];
        button.dataset.stepState = s.state;
        button.disabled = !s.reachable;
        if (s.state === 'current') button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
        button.setAttribute('aria-label', s.state === 'done' ? `${s.label}, done` : s.label);
      });
    }
    function paint(focus) {
      const c = configuration();
      section.dataset.builderStage = stages[index];
      section.querySelectorAll('.ndr-step[data-flow-stage], .ndr-summary__details[data-flow-stage]').forEach(node => {
        node.hidden = node.dataset.flowStage !== stages[index];
      });
      section.querySelectorAll('[data-builder-no-drill]').forEach(node => {node.hidden = c.options?.installation_method !== 'no-drill';});
      // The wall material card follows the saved order field (Back, edits, Add another window).
      form.querySelectorAll('[name="mountMaterial"]').forEach(input => {input.checked = input.value === (c.openingMaterial || '');});
      back.hidden = index === 0;
      next.hidden = index === stages.length - 1;
      add.hidden = index !== stages.length - 1;
      next.disabled = !canAdvance(stages[index], c, rules, core);
      progress.textContent = `Step ${index + 1} of ${stages.length}`;
      bar.style.width = `${(index + 1) / stages.length * 100}%`;
      visited.add(index);
      paintSteps(c);
      const quote = displayPrice(c, globalThis.NoDrillTieredPricePreview, rules, core);
      const sale = salePrice(quote);
      // Whole dollars normally; exact cents while the sale runs, so the builder matches checkout (Albert, Nov 2).
      const money = cents => new Intl.NumberFormat('en-US', sale
        ? {style:'currency', currency:quote.currency || 'USD', minimumFractionDigits:2, maximumFractionDigits:2}
        : {style:'currency', currency:quote.currency || 'USD', maximumFractionDigits:0}).format(cents / 100);
      const text = quote.valid ? `${quote.starting ? 'From ' : ''}${money(sale ? sale.totalCents : quote.totalCents)}` : 'Pick your options';
      price.textContent = text;
      if (regular) {
        regular.hidden = !sale;
        regular.firstElementChild.textContent = sale ? `Regular price ${money(quote.totalCents)}` : '';
      }
      hidePendingBadges(section, c);
      section.querySelectorAll('[data-live-price], [data-preview-price], [data-summary-price]').forEach(node => {node.textContent = text;});
      // The name hint shows the automatic name once a room is picked ("Kitchen, Window 2").
      const auto = roomReady(c) ? autoWindowName({room:c.room}, cartCounts, section.dataset.editLine ? ownLine : null) : '';
      section.dataset.autoWindowName = auto;
      form.elements.roomLabel.placeholder = auto;
      if (focus) {
        const stage = section.querySelector(`[data-flow-stage="${stages[index]}"]`);
        // Show "Step N of 7" with the question; on phones it otherwise slides under the sticky picture.
        const target = progress.closest('.ndr-builder-progress') || stage;
        stage?.setAttribute('tabindex', '-1');
        // Desktop leaves the page still while the step row is on screen; it only scrolls back when the shopper is below it.
        const desktop = globalThis.matchMedia?.('(min-width:800px)').matches;
        const top = target ? target.getBoundingClientRect().top : 0;
        if (!desktop || top < 0 || top > globalThis.innerHeight / 2) target?.scrollIntoView({block:'start', behavior:'instant'});
        stage?.focus({preventScroll:true});
      }
    }
    function go(step, push = true) {
      if (!Number.isInteger(step) || step < 0 || step >= stages.length) return;
      const previous = index;
      index = step;
      if (push) history.pushState(historyState(history.state, id, index, previous), '', globalThis.location.href);
      paint(true);
    }
    if (historyStep(history.state, id) === null) history.replaceState(historyState(history.state, id, 0), '', globalThis.location.href);
    next.addEventListener('click', () => { if (canAdvance(stages[index], configuration(), rules, core)) go(index + 1); });
    back.addEventListener('click', () => {
      if (index === 0) return;
      if (history.state?.ndrBuilder?.previous === index - 1) history.back();
      else go(index - 1);
    });
    globalThis.addEventListener('popstate', event => {
      const step = historyStep(event.state, id);
      if (step !== null) {
        // An edited shade already saved must not regain its stale edit URL on Back.
        if (!section.dataset.editLine && new URLSearchParams(location.search).has('edit_line')) {
          const url = new URL(location.href); url.searchParams.delete('edit_line');
          history.replaceState(event.state, '', url.pathname + url.search + url.hash);
        }
        go(step, false);
      }
    });
    // A tap on the step row goes to that step, the same way a Change link does, if the step can open.
    nav?.addEventListener('click', event => {
      const button = event.target.closest('[data-step-index]');
      if (!button) return;
      const step = Number(button.dataset.stepIndex);
      const states = stepStates(configuration(), rules, core, index, visited, Boolean(section.dataset.editLine));
      if (!states[step]?.reachable) return;
      if (step !== index) go(step);
    });
    section.addEventListener('click', event => {
      const edit = event.target.closest('[data-builder-change], [data-edit-headrail], [data-edit-bottom-rail]');
      if (!edit) return;
      event.preventDefault();
      const stage = edit.dataset.builderChange || (edit.hasAttribute('data-edit-headrail') ? 'hardware_finish' : 'bottom_rail');
      go(stages.indexOf(stage));
    });
    section.addEventListener('ndr:cart-window-names', event => {
      const editing = section.dataset.editLine;
      cartCounts = roomWindowCounts(event.detail, editing);
      const own = editing && (event.detail?.items || []).find(item => item?.properties?._ndr_line_role === 'shade'
        && item.properties._configuration_id === editing);
      ownLine = own ? {room:lineRoom(own.properties), label:lineLabel(own.properties)} : null;
      paint(false);
    });
    // Sent by the Add to cart pop-up. The configurator redraws from the cleared fields, then the
    // shopper lands on the size step with everything else kept.
    section.addEventListener('ndr:add-another-window', () => {
      resetForAnotherWindow(form);
      form.dispatchEvent(new Event('change', {bubbles:true}));
      go(stages.indexOf('measurements'));
    });
    if (section.dataset.editLine && index === 0) {
      for (let step = 1; step < stages.length; step++) go(step);
    }
    section.builderSteps = {refresh:() => paint(false), go};
    paint(false);
    // Until this flag is set, no-drill-builder-steps.css shows only step 1, so the page never flashes every step.
    section.dataset.builderReady = 'true';
    return section.builderSteps;
  }
  return Object.freeze({SALE, saleActive, salePrice, stages, stepLabels, stepStates, rooms, roomReady, roomWindowCounts, autoWindowName, resetForAnotherWindow, nextWindowName, windowName, historyState, historyStep, canAdvance, displayPrice, reviewRows, hidePendingBadges, setup});
});
